import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { money } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/deals.css';

const INCOTERMS = ['EXW', 'FOB', 'FCA', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];

function CreateDealModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [cps, setCps] = useState<Array<{ id: string; name: string }>>([]);
  const [title, setTitle] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [value, setValue] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [incoterm, setIncoterm] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { api.get<Array<{ id: string; name: string }>>('/counterparties').then(setCps).catch(() => void 0); }, []);

  const submit = async () => {
    if (title.trim().length < 2) { setErr('The title is required.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.post('/deals', {
        title: title.trim(),
        counterpartyId: counterpartyId || undefined,
        valueMinor: value.trim() === '' ? 0 : Math.round(Number(value) * 100),
        currency: currency.trim() || 'USD',
        incoterm: incoterm || undefined,
        origin: origin.trim() || undefined,
        destination: destination.trim() || undefined,
        sector: 'trading',
      });
      onCreated();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not create the deal.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div><span className="eyebrow">Nueva</span><h2 className="h2" style={{ marginTop: 4, fontSize: 20 }}>New deal</h2></div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          <div className="field"><label>Deal title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="p. ej. Urea granular 25.000 TM" autoFocus /></div>
          <div className="field">
            <label>Contraparte</label>
            <select className="select" value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
              <option value="">— Sin especificar —</option>
              {cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 160 }}><label>Deal value</label><input className="input" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" /></div>
            <div className="field" style={{ width: 100 }}><label>Moneda</label><input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} /></div>
            <div className="field" style={{ width: 130 }}>
              <label>Incoterm</label>
              <select className="select" value={incoterm} onChange={(e) => setIncoterm(e.target.value)}>
                <option value="">—</option>
                {INCOTERMS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 160 }}><label>Origen</label><input className="input" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="p. ej. Jebel Ali, AE" /></div>
            <div className="field grow" style={{ minWidth: 160 }}><label>Destino</label><input className="input" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="p. ej. Nanjing, CN" /></div>
          </div>
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>{busy ? 'Creando…' : 'Create deal'}</button>
        </div>
      </div>
    </div>
  );
}

export interface Deal {
  id: string;
  code: string;
  title: string;
  stage: string;
  stageLabel: string;
  stageIndex: number;
  stageCount: number;
  sector: string | null;
  counterparty: { id: string; name: string } | null;
  valueMinor: number;
  currency: string;
  incoterm: string | null;
  origin: string | null;
  destination: string | null;
  meta: Record<string, unknown>;
  isOpen: boolean;
  documents: Array<{ id: string; type: string; number: string; status: string }>;
}

export function Deals() {
  const { company } = useSession();
  const nav = useNavigate();
  const [rows, setRows] = useState<Deal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    if (!company) return;
    setRows(null);
    api.get<Deal[]>('/deals').then(setRows).catch((e) => setErr(e instanceof Error ? e.message : 'Error'));
  };
  useEffect(load, [company]);

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Operaciones</span>
            <h1 className="h1">Deal Rooms</h1>
            <p className="page-head__sub">Deal rooms for international trade. Each goes through fourteen stages, from intent to delivery and collection.</p>
          </div>
          <button className="btn btn--primary" onClick={() => setCreating(true)}><Icon name="plus" size={16} />New deal</button>
        </div>
      </div>

      <div className="card">
        {err && <div className="empty">{err}</div>}
        {!err && !rows && <div className="empty">Cargando operaciones…</div>}
        {!err && rows && rows.length === 0 && <div className="empty">No open deals yet.</div>}

        {!err && rows && rows.map((d) => (
          <button key={d.id} className="deal-card" onClick={() => nav(`/operaciones/${d.id}`)}>
            <div>
              <div className="deal-card__code">{d.code}</div>
              <div className="deal-card__title">{d.title}</div>
              <div className="deal-card__meta">
                {d.counterparty && <span>{d.counterparty.name}</span>}
                {d.origin && d.destination && <span>{d.origin} → {d.destination}</span>}
                {d.incoterm && <span className="type-tag">{d.incoterm}</span>}
                {d.documents.length > 0 && <span>{d.documents.length} documento(s)</span>}
              </div>
              <div className="stage-mini">
                {Array.from({ length: d.stageCount }).map((_, i) => (
                  <span key={i} className={i < d.stageIndex ? 'done' : i === d.stageIndex ? 'current' : ''} />
                ))}
              </div>
            </div>
            <div>
              <div className="deal-card__value">{money(d.valueMinor, d.currency)}</div>
              <div className="deal-card__stage">
                <Icon name="route" size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {d.stageIndex + 1}/{d.stageCount} · {d.stageLabel}
              </div>
            </div>
          </button>
        ))}
      </div>

      {creating && <CreateDealModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </main>
  );
}
